import { hasAnyForecast } from './forecastHelpers.js';

/**
 * Chips de filtre par type affichés sous le toggle Actifs/Passés.
 * Les modes sans chip (budget, provision, revenue) restent visibles sous « Tous ».
 */
export const FORECAST_TYPE_FILTERS = [
  { value: 'all',            label: 'Tous' },
  { value: 'recurring',      label: 'Récurrent' },
  { value: 'one_shot',       label: 'Ponctuel' },
  { value: 'monthly_budget', label: 'Budget mens.' },
  { value: 'indexed',        label: 'Indexé' },
  { value: 'debt',           label: 'Dette' },
];

/**
 * Normalise une chaîne pour une recherche insensible à la casse et aux accents.
 */
export function normalizeSearchText(value) {
  if (value == null) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function itemLabel(item) {
  return item.name ?? item.label ?? '';
}

function filterCategory(cat, needle, mode, ancestorMatches) {
  const nameMatches = ancestorMatches || (needle !== '' && normalizeSearchText(cat.name).includes(needle));

  const items = (cat.items ?? []).filter((item) => {
    if (mode !== 'all' && item.mode !== mode) return false;
    if (needle === '' || nameMatches) return true;
    return normalizeSearchText(itemLabel(item)).includes(needle);
  });

  const children = (cat.children ?? [])
    .map((child) => filterCategory(child, needle, mode, nameMatches))
    .filter(hasAnyForecast);

  // `total_monthly_cents` vient du backend et couvre TOUS les items de la
  // catégorie : le conserver ferait afficher le total non filtré à côté d'une
  // liste filtrée (sumCategoryMonthlyCents court-circuite dessus). On le retire
  // pour que le total soit recalculé sur les items retenus.
  const { total_monthly_cents: _unfilteredTotal, ...rest } = cat;
  return { ...rest, items, children };
}

/**
 * Filtre l'arbre des prévisionnels côté client (recherche texte + type d'item).
 * Une catégorie dont plus aucun item ne matche disparaît.
 *
 * @param {Array} categories  catégories racines déjà enrichies (indexed inclus)
 * @param {{ search?: string, mode?: string }} options
 * @returns {Array} nouvel arbre (les catégories source ne sont jamais mutées)
 */
export function filterForecastCategories(categories, { search = '', mode = 'all' } = {}) {
  const needle = normalizeSearchText(search);
  if (needle === '' && mode === 'all') return categories ?? [];
  return (categories ?? [])
    .map((cat) => filterCategory(cat, needle, mode, false))
    .filter(hasAnyForecast);
}

/**
 * Ids des catégories rendues comme section repliable (celles qui ont des enfants),
 * récursivement — utilisé par « Tout replier / Tout déplier ».
 */
export function collectSectionIds(categories) {
  const ids = [];
  const walk = (cat) => {
    if ((cat.children?.length ?? 0) === 0) return;
    ids.push(cat.id);
    for (const child of cat.children) walk(child);
  };
  for (const cat of categories ?? []) walk(cat);
  return ids;
}
